import { useState, useEffect, useRef } from "react"
import { libraryName } from "@/lib/libraryName"
import { photoCardState } from "@/lib/photoCardState"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiRequest } from "@/lib/queryClient"
import { useLocation } from "wouter"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import { FloatingCard } from "@/components/FloatingCard"
import { PhotoPreviewModal } from "@/components/PhotoPreviewModal"
import { ContentUploadModal } from "@/components/ContentUploadModal"
import { UploadReviewCard } from "@/components/UploadReviewCard"
import { HotelVideoLibrary } from "@/components/HotelVideoLibrary"
import { 
  Images, 
  Download, 
  Calendar, 
  Search,
  ArrowUpDown,
  AlertCircle,
  ExternalLink,
  Eye,
  FileImage,
  FileText,
  Camera,
  Clock,
  Loader2,
  CheckCircle2,
  Trash2
} from "lucide-react"
import { format } from "date-fns"
import { de } from "date-fns/locale"
import type { ContentUpload, User, GeneratedPost } from "@shared/schema"
import { useEdition } from "@/lib/edition"

export default function FotoBibliothek() {
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState<string>("newest")
  const [selectedUpload, setSelectedUpload] = useState<ContentUpload | null>(null)
  const [deleteConfirmUpload, setDeleteConfirmUpload] = useState<ContentUpload | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [, setLocation] = useLocation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  // Hotel-Edition (PLAN §6.3): hotel texts by edition; the behaviour follows the DATA — a `received` photo
  // (stored directly, HOTEL_UPLOADS_DIRECT_ENABLED) has no review step and no LinkedIn preview, while `pending`
  // photos (switch off) keep today's review flow. Both still go to the n8n photo workflow (Mike 2026-10-09), so a
  // `received` photo shows its generated posts once they arrive. Agencies unchanged.
  const editionState = useEdition()
  const isHotel = editionState.status === "ready" && editionState.edition.kind === "hotel"

  const deleteMutation = useMutation({
    mutationFn: async (uploadId: string) => {
      await apiRequest('DELETE', `/api/uploads/${uploadId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/uploads/my-approved'] })
      queryClient.invalidateQueries({ queryKey: ['/api/uploads/my-pending'] })
      toast({
        title: "Gelöscht",
        description: "Das Foto wurde gelöscht."
      })
      setDeleteConfirmUpload(null)
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler",
        description: error.message || "Löschen fehlgeschlagen.",
        variant: "destructive"
      })
    }
  })

  // Fetch user data for upload modal
  const { data: user } = useQuery<User>({
    queryKey: ['/api/auth/me']
  })

  // Fetch pending uploads (waiting for approval)
  const { data: pendingUploads = [] } = useQuery<ContentUpload[]>({
    queryKey: ['/api/uploads/my-pending'],
    enabled: !!user,
    refetchInterval: 5000 // Refresh every 5 seconds to catch new uploads
  })

  // Fetch approved uploads - poll if any are still processing
  const { data: approvedUploads = [], isLoading, error } = useQuery<ContentUpload[]>({
    queryKey: ['/api/uploads/my-approved'],
    refetchInterval: (query) => {
      const data = query.state.data as ContentUpload[] | undefined
      // If any upload is still processing via n8n. A `received` hotel photo is sent right after it is stored, so
      // only 'sent'/'processing' count for it ('not_sent' there = no n8n configured, nothing will come).
      const hasProcessing = data?.some(upload =>
        upload.n8nProcessingStatus === 'sent' ||
        upload.n8nProcessingStatus === 'processing' ||
        (upload.status !== 'received' && upload.n8nProcessingStatus === 'not_sent')
      )
      return hasProcessing ? 5000 : false
    }
  })

  // Auto-resume publishing after OAuth return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const publishPostId = urlParams.get('publishPostId')
    
    if (publishPostId && approvedUploads.length > 0) {
      // Store in sessionStorage for PhotoPreviewModal to handle
      sessionStorage.setItem('pendingPublishPostId', publishPostId)
      
      // Clear URL param
      urlParams.delete('publishPostId')
      const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '')
      window.history.replaceState({}, '', newUrl)
      
      toast({
        title: "Plattform verbunden",
        description: "Bitte öffnen Sie das Foto, um fortzufahren.",
      })
    }
  }, [approvedUploads, toast])

  // Deep-link from the Posting-Plan: ?upload=<id> opens that upload's Post Vorschau modal.
  const didOpenUploadRef = useRef(false)
  useEffect(() => {
    if (didOpenUploadRef.current || approvedUploads.length === 0) return
    const uploadId = new URLSearchParams(window.location.search).get('upload')
    if (!uploadId) return
    const target = approvedUploads.find(u => u.id === uploadId)
    if (target) {
      didOpenUploadRef.current = true
      setSelectedUpload(target)
    }
  }, [approvedUploads])

  // Filter and sort uploads
  const filteredAndSortedUploads = approvedUploads
    .filter(upload => {
      if (!searchTerm) return true
      return upload.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
             upload.message?.toLowerCase().includes(searchTerm.toLowerCase())
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.createdAt || b.updatedAt || new Date()).getTime() - new Date(a.createdAt || a.updatedAt || new Date()).getTime()
        case "oldest":
          return new Date(a.createdAt || a.updatedAt || new Date()).getTime() - new Date(b.createdAt || b.updatedAt || new Date()).getTime()
        case "name":
          return a.originalName.localeCompare(b.originalName)
        case "size":
          return b.fileSize - a.fileSize
        default:
          return 0
      }
    })

  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString
    return format(date, 'dd.MM.yyyy HH:mm', { locale: de })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const downloadImage = async (upload: ContentUpload) => {
    try {
      const response = await fetch(upload.filePath)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = upload.originalName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      toast({
        title: "Download gestartet",
        description: `${upload.originalName} wird heruntergeladen`
      })
    } catch (error) {
      toast({
        title: "Download-Fehler",
        description: "Das Bild konnte nicht heruntergeladen werden",
        variant: "destructive"
      })
    }
  }

  const openImageInNewTab = (upload: ContentUpload) => {
    window.open(upload.filePath, '_blank')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700 p-6">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-xl border bg-card overflow-hidden">
                <Skeleton className="aspect-square w-full rounded-none" />
                <div className="p-4 space-y-3">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-1/3" />
                  <Skeleton className="h-6 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Fehler beim Laden</h3>
            <p className="text-muted-foreground">Die Fotos konnten nicht geladen werden.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
            <Images className="h-8 w-8 text-primary" aria-hidden="true" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-purple-600 to-primary bg-clip-text text-transparent">
            {libraryName(isHotel)}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {isHotel ? "Ihre Fotos für Ihre Beiträge. Ihr Team sieht jedes Foto sofort." : "Alle freigegebenen Fotos für Ihre Beiträge"}
          </p>
        </div>

        {/* Stats and Controls */}
        <div>
          <FloatingCard>
            <CardHeader>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <FileImage className="h-5 w-5" aria-hidden="true" />
                    <span>{isHotel ? "Ihre Fotos" : "Freigegebene Fotos"}</span>
                    <Badge variant="secondary" data-testid="approved-count">
                      {approvedUploads.length}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {isHotel ? "Ihr Team nutzt sie für Ihre Beiträge" : "Bereit für Ihre Beiträge"}
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setShowUploadModal(true)}
                  disabled={!user}
                  className="gap-2"
                  data-testid="button-upload-photo-library"
                >
                  <Camera className="h-4 w-4" aria-hidden="true" />
                  Foto hochladen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Fotos durchsuchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="input-search-photos"
                  />
                </div>
                
                {/* Sort */}
                <div className="flex items-center space-x-2">
                  <ArrowUpDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[180px]" data-testid="select-sort-photos">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Neueste zuerst</SelectItem>
                      <SelectItem value="oldest">Älteste zuerst</SelectItem>
                      <SelectItem value="name">Nach Name</SelectItem>
                      <SelectItem value="size">Nach Größe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </FloatingCard>
        </div>

        {/* Pending Uploads Review Section */}
        {pendingUploads.length > 0 && (
          <div className="mb-8" data-testid="pending-uploads-section">
            <Card className="bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                      {pendingUploads.length} {pendingUploads.length === 1 ? 'Foto' : 'Fotos'} warten auf Freigabe
                    </h3>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Prüfen und genehmigen Sie Ihre hochgeladenen Fotos, damit Ihr Team daraus Beiträge erstellt.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pendingUploads.map((upload) => (
                    <UploadReviewCard key={upload.id} upload={upload} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Photo Gallery */}
        {filteredAndSortedUploads.length === 0 ? (
          <FloatingCard>
            <CardContent className="text-center py-12">
              <Images className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {searchTerm ? "Keine Fotos gefunden" : isHotel ? "Noch keine Fotos" : "Noch keine freigegebenen Fotos"}
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                {searchTerm
                  ? "Versuchen Sie einen anderen Suchbegriff."
                  : isHotel
                    ? "Sobald Sie Fotos hochladen, erscheinen sie hier."
                    : "Sobald Sie Fotos hochladen und freigeben, erscheinen sie hier."}
              </p>
            </CardContent>
          </FloatingCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAndSortedUploads.map((upload, index) => (
              <PhotoCard
                key={upload.id}
                upload={upload}
                onDownload={() => downloadImage(upload)}
                onViewFull={() => openImageInNewTab(upload)}
                onViewPosts={() => setSelectedUpload(upload)}
                onDelete={() => setDeleteConfirmUpload(upload)}
                formatDate={formatDate}
                formatFileSize={formatFileSize}
                isHotel={isHotel}
                received={upload.status === 'received'}
              />
            ))}
          </div>
        )}

        {/* Hotel videos (PLAN.md §23): only for hotels, only once a video exists */}
        <HotelVideoLibrary enabled={isHotel} />
      </div>


      {/* Photo Preview Modal */}
      {selectedUpload && (
        <PhotoPreviewModal
          open={!!selectedUpload}
          onOpenChange={(open) => !open && setSelectedUpload(null)}
          upload={selectedUpload}
        />
      )}

      {/* Upload Modal — lets you add a photo directly from the library (not only the Dashboard) */}
      {user && (
        <ContentUploadModal
          open={showUploadModal}
          onOpenChange={setShowUploadModal}
          user={user}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmUpload} onOpenChange={(open) => !open && setDeleteConfirmUpload(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Foto „{deleteConfirmUpload?.originalName}" löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Alle zu diesem Foto gehörenden Posts werden ebenfalls gelöscht.
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmUpload && deleteMutation.mutate(deleteConfirmUpload.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Löschen..." : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface PhotoCardProps {
  upload: ContentUpload
  onDownload: () => void
  onViewFull: () => void
  onViewPosts: () => void
  onDelete: () => void
  formatDate: (date: string | Date) => string
  formatFileSize: (bytes: number) => string
  isHotel?: boolean
  /** Stored directly (hotel, status `received`): no review step; its n8n posts show once they arrive. */
  received?: boolean
}

function PhotoCard({ upload, onDownload, onViewFull, onViewPosts, onDelete, formatDate, formatFileSize, isHotel = false, received = false }: PhotoCardProps) {
  const [imageLoading, setImageLoading] = useState(true)
  const [imageError, setImageError] = useState(false)

  // Fetch generated posts for this upload. Poll ONLY while n8n is actively processing it and
  // no posts exist yet. 'not_sent' is deliberately excluded — an approved upload stuck
  // pre-dispatch would otherwise poll forever; the parent list poll watches for it to flip to
  // 'sent', which re-renders this card and starts the poll then.
  const { data: posts = [], isError, refetch } = useQuery<GeneratedPost[]>({
    queryKey: ['/api/generated-posts', upload.id],
    queryFn: async () => {
      const response = await fetch(`/api/generated-posts?uploadId=${upload.id}`)
      // Throw (not return []) so a transient failure becomes a distinct error state instead of
      // masquerading as "no posts" — global retry is off, so the card shows a retry affordance.
      if (!response.ok) throw new Error('Posts konnten nicht geladen werden')
      return response.json()
    },
    // NOTE: the function form of refetchInterval is React Query v5 API.
    refetchInterval: (query) => {
      const data = query.state.data as GeneratedPost[] | undefined
      if (data?.length) return false // done — posts arrived
      const status = upload.n8nProcessingStatus ?? ''
      if (status === 'sent' || status === 'processing') return 4000
      // Grace window: n8n's post insert and the status flip to 'completed' are not atomic, so
      // posts can land a moment after status === 'completed'. Bound by dataUpdateCount (which
      // increments per successful FETCH, not per re-render), so unrelated re-renders can't
      // exhaust the window early; stops for good after ~4 fetches.
      if (status === 'completed' && query.state.dataUpdateCount < 4) return 4000
      return false
    },
    gcTime: 300000, // Keep in cache for 5 minutes
  })

  const [, setLocation] = useLocation()

  const postsCount = posts.length
  const cardState = photoCardState({ postsCount, isError, received, n8nProcessingStatus: upload.n8nProcessingStatus })
  const hasLinkedInPost = posts.find(p => p.platform === 'linkedin')
  const linkedInPreview = hasLinkedInPost?.postContent.substring(0, 80)

  // Earliest scheduled post for this photo (if any) — surfaces the planned date on the card.
  const scheduledPost = posts
    .filter(p => (p as any).postingStatus === 'scheduled' && (p as any).scheduledFor)
    .sort((a, b) => new Date((a as any).scheduledFor).getTime() - new Date((b as any).scheduledFor).getTime())[0]

  return (
    <FloatingCard className="group transition-all duration-300">
      <CardContent className="p-0">
        {/* Image Preview */}
        <div className="relative aspect-square bg-muted rounded-t-lg overflow-hidden">
          {upload.filePath && !imageError ? (
            <>
              {imageLoading && (
                <div className="absolute inset-0 bg-primary/10 rounded-t-lg flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                </div>
              )}
              <img
                src={upload.filePath}
                alt={upload.originalName}
                loading="lazy"
                decoding="async"
                className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${
                  imageLoading ? 'opacity-0' : 'opacity-100'
                }`}
                onLoad={() => setImageLoading(false)}
                onError={() => {
                  setImageError(true)
                  setImageLoading(false)
                }}
                data-testid={`image-${upload.id}`}
              />
            </>
          ) : (
            <div className="w-full h-full bg-muted flex flex-col items-center justify-center gap-1 p-2 text-center">
              <AlertCircle className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
              <span className="text-xs text-muted-foreground">Bild konnte nicht geladen werden</span>
              <span className="text-xs text-muted-foreground truncate max-w-full">{upload.originalName}</span>
            </div>
          )}

          {/* Action overlay — rendered regardless of image-load state so a broken thumbnail
              never loses its view/download/delete controls. Revealed on hover, on keyboard
              focus (focus-within), and always on touch devices (which have no hover). */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={onViewFull}
              aria-label="Bild ansehen"
              data-testid={`button-view-${upload.id}`}
            >
              <Eye className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={onDownload}
              aria-label="Herunterladen"
              data-testid={`button-download-${upload.id}`}
            >
              <Download className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={onDelete}
              aria-label="Löschen"
              data-testid={`button-delete-${upload.id}`}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        {/* Photo Info */}
        <div className="p-4 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-medium text-sm truncate" data-testid={`photo-name-${upload.id}`}>
                {upload.originalName}
              </h3>
              {postsCount > 0 && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                  {postsCount} {postsCount === 1 ? 'Post' : 'Posts'}
                </Badge>
              )}
            </div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" aria-hidden="true" />
              <span>{formatDate(upload.createdAt || upload.updatedAt || new Date())}</span>
            </div>
          </div>

          {scheduledPost && (
            <button
              type="button"
              onClick={() => setLocation(`/veroeffentlichungsplan?post=${scheduledPost.id}`)}
              className="inline-flex w-fit items-center gap-1 rounded-full border border-blue-300 dark:border-blue-700 px-2 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors"
              title="Im Posting-Plan ansehen"
              data-testid={`badge-scheduled-${upload.id}`}
            >
              <Clock className="h-3 w-3" aria-hidden="true" />
              Geplant für {format(new Date((scheduledPost as any).scheduledFor), "dd.MM. HH:mm", { locale: de })}
            </button>
          )}

          {/* Content Preview */}
          {!isHotel && linkedInPreview && (
            <p className="text-xs text-muted-foreground line-clamp-2" data-testid={`linkedin-preview-${upload.id}`}>
              <span className="font-medium">LinkedIn:</span> {linkedInPreview}...
            </p>
          )}

          {/* User Message — keep the user's own note visible even after posts are generated */}
          {upload.message && (
            <p className="text-xs text-muted-foreground line-clamp-1 italic" data-testid={`photo-message-${upload.id}`}>
              "{upload.message}"
            </p>
          )}

          <div className="flex items-center justify-between gap-2">
            <Badge variant="outline" className="text-xs">
              {formatFileSize(upload.fileSize)}
            </Badge>
            
            {cardState === "received" && upload.n8nProcessingStatus === "skipped" ? (
              // Check-in photo (Mike 2026-10-10, Variante A): only stored, no posts will come.
              <span className="text-xs text-muted-foreground truncate max-w-[60%]" data-testid={`status-stored-${upload.id}`}>
                Gespeichert{upload.pillarName ? ` · ${upload.pillarName}` : ""}
              </span>
            ) : cardState === "received" ? (
              upload.pillarName ? (
                <Badge variant="secondary" className="text-xs truncate max-w-[60%]" data-testid={`photo-topic-${upload.id}`}>
                  {upload.pillarName}
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground" data-testid={`status-received-${upload.id}`}>
                  Bei Ihrem Team
                </span>
              )
            ) : cardState === "posts" ? (
              <Button
                size="sm"
                onClick={onViewPosts}
                className="h-7 text-xs gap-1"
                aria-label="Alle Posts ansehen"
                data-testid={`button-view-posts-${upload.id}`}
              >
                <FileText className="h-3 w-3" aria-hidden="true" />
                Alle Posts
              </Button>
            ) : cardState === "fetch-error" ? (
              <button
                type="button"
                onClick={() => refetch()}
                className="flex items-center gap-1 text-xs text-destructive hover:underline"
                data-testid={`status-error-${upload.id}`}
              >
                <AlertCircle className="h-3 w-3" aria-hidden="true" />
                Fehler, erneut laden
              </button>
            ) : cardState === "failed" ? (
              <div className="flex items-center gap-1 text-xs text-destructive" data-testid={`status-failed-${upload.id}`}>
                <AlertCircle className="h-3 w-3" aria-hidden="true" />
                Verarbeitung fehlgeschlagen
              </div>
            ) : cardState === "empty" ? (
              <span className="text-xs text-muted-foreground" data-testid={`status-empty-${upload.id}`}>
                Keine Posts generiert
              </span>
            ) : (
              <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid={`status-processing-${upload.id}`}>
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                Wird verarbeitet...
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </FloatingCard>
  )
}